/**
 * Shared formatting utilities for the Online Sales Journey.
 * Single source of truth — used by Quote, Summary, and other components.
 */

import { COVER_AMOUNT_RUPEE_STEP } from '../../constants';

const parseDateInput = (raw) => {
  if (!raw) return null;
  const value = String(raw).trim();
  const dmyMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (dmyMatch) {
    const d = Number(dmyMatch[1]);
    const m = Number(dmyMatch[2]);
    const y = Number(dmyMatch[3]);
    const localDate = new Date(y, m - 1, d);
    if (
      Number.isNaN(localDate.getTime()) ||
      localDate.getFullYear() !== y ||
      localDate.getMonth() + 1 !== m ||
      localDate.getDate() !== d
    ) {
      return null;
    }
    return localDate;
  }
  // Parse date-only string in local time to avoid UTC shift.
  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const y = Number(isoMatch[1]);
    const m = Number(isoMatch[2]);
    const d = Number(isoMatch[3]);
    const localDate = new Date(y, m - 1, d);
    if (
      Number.isNaN(localDate.getTime()) ||
      localDate.getFullYear() !== y ||
      localDate.getMonth() + 1 !== m ||
      localDate.getDate() !== d
    ) {
      return null;
    }
    return localDate;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

/**
 * Format a number as Indian currency with ₹ symbol.
 * @param {number|string|null|undefined} value
 * @returns {string} e.g. "₹ 3,403" or "--"
 */
export const formatCurrency = (value) => {
  if (value === undefined || value === null || value === '') return '--';
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return String(value);
  return `₹ ${numberValue.toLocaleString('en-IN')}`;
};

/**
 * Parse user input for cover amount into numeric value (as API expects).
 * Accepts: digits only (e.g. 24000000), or "2.4"/"2.4cr" (crore), or "50lakh".
 * @param {string} str - Raw input
 * @returns {number|null} Amount in rupees or null if invalid
 */
export const parseCoverAmountInput = (str) => {
  if (str == null || typeof str !== 'string') return null;
  const trimmed = str.trim();
  if (!trimmed) return null;
  // Pure digits → amount in rupees (e.g. 24000000)
  const digitsOnly = trimmed.replace(/\D/g, '');
  if (digitsOnly === trimmed) {
    const n = parseInt(digitsOnly, 10);
    return Number.isNaN(n) ? null : n;
  }
  // "2.4", "2.4cr", "2.4 cr", "50lakh", "50 lakh"
  const match = trimmed.match(/^(\d+(?:\.\d+)?)\s*(cr|lakh)?$/i);
  if (!match) return null;
  const num = parseFloat(match[1]);
  if (Number.isNaN(num)) return null;
  const unit = (match[2] || '').toLowerCase();
  if (unit === 'lakh') return Math.round(num * 100000);
  if (unit === 'cr' || (unit === '' && (num >= 1 || /\./.test(match[1])))) return Math.round(num * 10000000);
  // Integer without unit → treat as full rupees
  return Math.round(num);
};

/**
 * Round sum assured to the same ₹1,00,000 step as grid options and clamp to [min, max].
 * Ensures backend Excel/workbook lookups receive aligned value
 * s.
 * @param {number} amount - Parsed rupee amount
 * @param {number} minAmount
 * @param {number} maxAmount
 * @returns {number|null}
 */
export const quantizeCoverAmountToRupeesStep = (amount, minAmount, maxAmount) => {
  const n = Number(amount);
  const min = Number(minAmount);
  const max = Number(maxAmount);
  if (Number.isNaN(n) || n <= 0 || Number.isNaN(min) || Number.isNaN(max)) return null;
  const step = COVER_AMOUNT_RUPEE_STEP;
  const rounded = Math.round(n / step) * step;
  const clamped = Math.min(max, Math.max(min, rounded));
  return clamped;
};

/**
 * Format a number as Lakhs / Crores shorthand.
 * @param {number|string|null|undefined} value
 * @returns {string} e.g. "50 Lakh" or "1.5 Cr"
 */
export const formatLakhs = (value) => {
  if (!value) return '';
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return String(value);
  if (numberValue >= 10000000) {
    return `${(numberValue / 10000000).toFixed(2)} Cr`;
  }
  return `${(numberValue / 100000).toFixed(0)} Lakh`;
};

/**
 * Format a number as Lakhs / Crores with ₹ prefix (used in Summary).
 * @param {number|string|null|undefined} value
 * @returns {string} e.g. "₹50 Lakh" or "₹1.5 Cr"
 */

/**
 * Calculate age from a date-of-birth string.
 * @param {string|null} dob - Date of birth (parseable by Date constructor)
 * @returns {number|null}
 */
export const calculateAge = (dob) => {
  if (!dob) return null;
  const birthDate = parseDateInput(dob);
  if (!birthDate || Number.isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};


export const isDobWithinAgeRange = (isoDob, { minAge, maxAge }) => {
  if (!isoDob || typeof isoDob !== "string") {
    return false;
  }

  const [year, month, day] = isoDob.split("-").map(Number);

  if (!year || !month || !day) {
    return false;
  }

  const dob = new Date(year, month - 1, day);

  if (
    dob.getFullYear() !== year ||
    dob.getMonth() !== month - 1 ||
    dob.getDate() !== day
  ) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  dob.setHours(0, 0, 0, 0);

  // Latest DOB allowed for minimum age
  const latestAllowedDob = new Date(
    today.getFullYear() - minAge,
    today.getMonth(),
    today.getDate()
  );

  
  const earliestAllowedDob = new Date(
    today.getFullYear() - (maxAge + 1),
    today.getMonth(),
    today.getDate() + 1
  );

  return dob >= earliestAllowedDob && dob <= latestAllowedDob;
};

/**
 * Format a date string as DD/MM/YYYY.
 * @param {string|null} dateStr - Date string parseable by Date constructor
 * @returns {string} e.g. "08/02/2026" or ""
 */
export const formatDateDDMMYYYY = (dateStr) => {
  if (!dateStr) return '';
  const date = parseDateInput(dateStr);
  if (!date || Number.isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Get ordinal suffix for a day number (1st, 2nd, 3rd, 4th, ...).
 * @param {number} day
 * @returns {string}
 */
export const getOrdinalSuffix = (day) => {
  if (day === 1 || day === 21 || day === 31) return 'st';
  if (day === 2 || day === 22) return 'nd';
  if (day === 3 || day === 23) return 'rd';
  return 'th';
};

/**
 * Premium revision date: (today - 1 day) next year, e.g. 30 June 2030 -> 29 June 2031.
 * @returns {string} e.g. "29th Jun"
 */
export const getPremiumRevisionDate = () => {
  const today = new Date();
  const nextYear = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate() - 1);
  const day = nextYear.getDate();
  const suffix = getOrdinalSuffix(day);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${day}${suffix} ${months[nextYear.getMonth()]}`;
};
